import { useId, useRef, useState } from 'react';
import { Radio as BaseRadio } from '@base-ui/react/radio';
import { RadioGroup as BaseRadioGroup } from '@base-ui/react/radio-group';
import { RiAddLine } from '@remixicon/react';
import { cn } from '@/shared/lib/cn';
import { FieldCaption } from './field';

export interface ColorPreset {
  value: string;
  /** Accessible name, e.g. "Ko'k". */
  label: string;
}

/** "Asosiy rang" presets (556:16647, spec §1.6). "Qizil" really is Red/500 (#ff000080). */
export const ACCENT_PRESETS: readonly ColorPreset[] = [
  { value: '#0275FF', label: "Ko'k" },
  { value: '#1ECD15', label: 'Yashil' },
  { value: '#509F67', label: "To'q yashil" },
  { value: '#F4B400', label: 'Sariq' },
  { value: '#F48200', label: "To'q sariq" },
  { value: '#FF000080', label: 'Qizil' },
  { value: '#DF0259', label: 'Pushti' },
  { value: '#7059FF', label: 'Binafsha' },
];

const CUSTOM = '__custom__';
const HEX_RE = /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

const normalize = (hex: string) => hex.trim().toUpperCase();

export interface ColorSwatchesProps {
  value: string;
  onValueChange: (hex: string) => void;
  presets?: readonly ColorPreset[];
  /** "+" swatch that opens the system colour picker. Default true. */
  allowCustom?: boolean;
  /** HEX field under the swatches. Default true. */
  showHexInput?: boolean;
  hexLabel?: React.ReactNode;
  customLabel?: string;
  /** `md` — 40 hit area / 32 circle (settings). `sm` — 22 circles, 2px ring (status colours, 02-constructor §2.5). */
  size?: 'md' | 'sm';
  disabled?: boolean;
  'aria-label'?: string;
  className?: string;
}

/**
 * Accent colour picker (556:16647 "Asosiy rang"): 32 px circles in 40 px hit areas (gap 10); the selected one gets a
 * 2px inside white ring and shrinks to 28. "Maxsus" (+) on White/10 opens the colour picker. Below: caps caption
 * "MAXSUS RANG (HEX)" + a 140×32 field (16 px r4 swatch + value 13/18).
 */
export function ColorSwatches({
  value,
  onValueChange,
  presets = ACCENT_PRESETS,
  allowCustom = true,
  showHexInput = true,
  hexLabel = 'Maxsus rang (HEX)',
  customLabel = 'Maxsus rang',
  size = 'md',
  disabled,
  'aria-label': ariaLabel = 'Asosiy rang',
  className,
}: ColorSwatchesProps) {
  const hexId = useId();
  const colorInputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState<string | null>(null);
  const current = normalize(value);
  const isPreset = presets.some((p) => normalize(p.value) === current);
  const selected = isPreset ? current : CUSTOM;
  const md = size === 'md';

  const commitDraft = () => {
    if (draft == null) return;
    const next = draft.startsWith('#') ? draft : `#${draft}`;
    if (HEX_RE.test(next)) onValueChange(normalize(next));
    setDraft(null);
  };

  // The selection ring is an ::after overlay so it paints above the colour dot (2px inside, White/1000).
  const swatchCls = cn(
    'group/sw relative flex shrink-0 cursor-pointer items-center justify-center rounded-full outline-none',
    'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent-2',
    'after:pointer-events-none after:absolute after:inset-0 after:rounded-full data-checked:after:shadow-[inset_0_0_0_2px_var(--color-fg)]',
    'data-disabled:cursor-not-allowed data-disabled:opacity-50',
    md ? 'size-40' : 'size-22',
  );
  const dotCls = cn(
    'block rounded-full transition-[width,height] duration-150',
    md ? 'size-32 group-data-checked/sw:size-28' : 'size-22',
  );

  return (
    <div className={cn('flex flex-col gap-14', className)}>
      <BaseRadioGroup
        aria-label={ariaLabel}
        value={selected}
        onValueChange={(next) => {
          if (next !== CUSTOM) onValueChange(normalize(String(next)));
        }}
        disabled={disabled}
        className="flex flex-wrap items-center gap-10"
      >
        {presets.map((preset) => (
          <BaseRadio.Root key={preset.value} value={normalize(preset.value)} aria-label={preset.label} className={swatchCls}>
            <span className={dotCls} style={{ backgroundColor: preset.value }} />
          </BaseRadio.Root>
        ))}
        {allowCustom && (
          <BaseRadio.Root
            value={CUSTOM}
            aria-label={customLabel}
            className={swatchCls}
            onClick={() => colorInputRef.current?.click()}
          >
            <span
              className={cn(dotCls, 'flex items-center justify-center', !isPreset ? '' : 'bg-field')}
              style={!isPreset ? { backgroundColor: value } : undefined}
            >
              {isPreset && <RiAddLine aria-hidden className="size-16 text-icon" />}
            </span>
          </BaseRadio.Root>
        )}
      </BaseRadioGroup>
      {allowCustom && (
        <input
          ref={colorInputRef}
          type="color"
          tabIndex={-1}
          aria-hidden
          className="sr-only"
          value={current.length === 7 ? current.toLowerCase() : '#0275ff'}
          onChange={(e) => onValueChange(normalize(e.currentTarget.value))}
        />
      )}
      {showHexInput && (
        <div className="flex items-center gap-10">
          <FieldCaption as="span">
            <label htmlFor={hexId}>{hexLabel}</label>
          </FieldCaption>
          <div className="flex h-32 w-140 items-center gap-8 rounded-8 border border-transparent bg-field pr-12 pl-10 focus-within:border-focus">
            <span aria-hidden className="size-16 shrink-0 rounded-4" style={{ backgroundColor: HEX_RE.test(draft ?? '') ? draft! : value }} />
            <input
              id={hexId}
              disabled={disabled}
              spellCheck={false}
              autoComplete="off"
              maxLength={9}
              value={draft ?? current}
              onChange={(e) => setDraft(e.currentTarget.value.toUpperCase())}
              onBlur={commitDraft}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitDraft();
                if (e.key === 'Escape') setDraft(null);
              }}
              className="min-w-0 flex-1 bg-transparent t-field text-fg outline-none"
            />
          </div>
        </div>
      )}
    </div>
  );
}
